import * as React from 'react';
import { rangeBetween } from '../utils/selection';

export function useActionItemSelection(orderedIds: string[]) {
	const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set());
	const anchorRef = React.useRef<string | null>(null);

	React.useEffect(() => {
		setSelectedIds(previous => {
			if (previous.size === 0) {
				return previous;
			}
			const present = new Set(orderedIds);
			const next = new Set([...previous].filter(id => present.has(id)));
			return next.size === previous.size ? previous : next;
		});
	}, [orderedIds]);

	const toggle = React.useCallback(
		(id: string, withRange: boolean) => {
			setSelectedIds(previous => {
				const next = new Set(previous);
				if (withRange && anchorRef.current) {
					for (const rangeId of rangeBetween(orderedIds, anchorRef.current, id)) {
						next.add(rangeId);
					}
				} else if (next.has(id)) {
					next.delete(id);
				} else {
					next.add(id);
				}
				return next;
			});
			anchorRef.current = id;
		},
		[orderedIds]
	);

	const clear = React.useCallback(() => {
		setSelectedIds(new Set());
		anchorRef.current = null;
	}, []);

	const replace = React.useCallback((ids: Iterable<string>) => setSelectedIds(new Set(ids)), []);

	return {
		selectedIds,
		toggle,
		clear,
		replace,
		isSelected: (id: string) => selectedIds.has(id),
		hasSelection: selectedIds.size > 0,
	};
}
